import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  ReactCalendarGroupRendererProps,
  TimelineGroup,
} from './react-calendar-timeline';
import { TimelineStyle } from './timelineStyles';
import { Box, Menu, MenuItem, Text } from '../ui';
import type { EventTimeline } from '../core/Timelines';
import { makeTimelineItemRenderer } from './ScenarioTimeline';
import { buildAssignedItems } from './utils/buildAssignedItems';
import type { ScenarioTimeline_OrganizationFragment } from '../../graphql';

import { scrollToToday, useTimelineHandlers } from './handlers';
import type { ITimelineView } from './TimelineView';
import TimelineView from './TimelineView';
import type {
  UserScenarioData,
  MasterScenarioData,
  ScenarioLocation,
  CommittedPlanningData,
  GetScenarioData,
} from '../../lib/planningData';
import type { ScenarioTimelineDiff } from '../ScenarioUtil';
import {
  createCommitInput,
  getScenarioTimelinesDiff,
  hasTimelineChanged,
  previewMasterScenarioFromCommitInput,
} from '../ScenarioUtil';
import { assertDefined, exhaustiveCheck, mapKeyBy } from '../../lib/util';
import {
  getScenarioOrdersAndShipments,
  isMasterScenario,
  isUserScenario,
  ScenarioCommitPolicy,
  MASTER_SCENARIO_CLIENTID,
  isSuggestionScenario,
} from '../Scenario';
import type {
  ScenarioSchedule,
  UserSchedule,
  SuggestionSchedule,
} from '@seaber/common/core/scenario';
import type { MergeDirectives } from '../MergeScenarios';
import { getMergedScenario } from '../MergeScenarios';
import { composeTypeGuardOr } from '../../lib/util';
import useContextMenu from '../hooks/useContextMenu';
import { compact, keyBy, uniq } from 'lodash';
import { getVesselCapacities } from '../core/Vessel';
import type { ScheduleDataContext } from '../core/ScheduleDataContext';
import type { TimeData } from '../core/TimeData';
import { isLoadEventTimeData, isTCOrderEventTimeData } from '../core/TimeData';
import type { CustomObserver } from './IntersectionObserver';
import { prepareIntersectionObserver } from './IntersectionObserver';
import type { ClientId } from '../core/ClientId';
import { useCurrentUser } from '../../state/zustand';
import type { ResetAssignmentsToMasterOpts } from '../../pages/planning';
import TimelineResizeDetector from './TimelineResizeDetector';
import { DefaultMap } from '@seaber/common/lib/collections';
import type { ScenarioTimelineItem } from './types';
import { ItemType } from './types';
import { VoyageMenuContext } from '../contexts/VoyageMenuContext';
import { useContextStore } from '../contexts/ZustandContext';
import { isEuEtsCostEnabledForUser } from '../shipment/income/income';
import { useCurrentScenarioPnl } from '../pnl/ui/context';

interface ScenarioMergeTimelineRef {
  scrollToToday: () => void;
}
interface ScenarioMergeTimelineProps {
  organizations: Map<number, ScenarioTimeline_OrganizationFragment>;
  scheduleContext: ScheduleDataContext;
  masterData: MasterScenarioData;
  selectedScenarioData: UserScenarioData;
  committedData: CommittedPlanningData;
  getScenarioData: GetScenarioData;
  mergeDirectives: MergeDirectives | null;
  moveShipmentAfterAnother: (
    shipmentToMove: ClientId,
    targetShipment: ClientId | null,
  ) => void;

  // Should be memoized
  filterTimelines: (timeline: EventTimeline) => boolean;
  onResetAssignmentsToMaster: (items: ResetAssignmentsToMasterOpts) => void;
  onConfirmEventOverwrite: (events: TimeData[]) => void;
  sharedOrderIds: Set<number>;
  commitPolicy?: ScenarioCommitPolicy;
}

// master => committed
export type ScenarioType = 'master' | 'scenario' | 'commitresult';

export type ScenarioMergeTimelineGroupFields = {
  scenarioType: ScenarioType;
  timeline: EventTimeline | null;
  name?: string | null;
};

export type ScenarioMergeTimelineItem = ScenarioTimelineItem;

type ScenarioMergeTimelineGroup =
  TimelineGroup<ScenarioMergeTimelineGroupFields>;

const generateGroupId = (
  timelineId: EventTimeline['id'],
  scenarioName: ScenarioMergeTimelineGroupFields['scenarioType'],
): string => `${scenarioName}-${timelineId}`;

const scenarioNameToLabel = (
  name: ScenarioMergeTimelineGroup['scenarioType'],
): string =>
  name === 'master'
    ? 'Committed'
    : name === 'scenario'
      ? 'Scenario'
      : name === 'commitresult'
        ? 'Result'
        : exhaustiveCheck(name);

const groupRenderer = ({
  group,
}: ReactCalendarGroupRendererProps<ScenarioMergeTimelineGroup>): React.ReactNode => {
  const borderColor =
    group.timeline?.type === 'coa' && group.timeline.vesselId == null
      ? 'neutral.gray_250'
      : 'transparent';

  const { vessel } = group.timeline ?? {};
  const capacities = vessel ? getVesselCapacities(vessel) : null;
  const massCapacityLabel = capacities
    ? `/ ${capacities.massCapacitySource === 'DWT' ? 'DWT ' : ''}${
        capacities.massCapacity
      } mts`
    : '';

  const isMasterScenarioInMultiCompare =
    group.name === '' && group.scenarioType === 'master';
  return (
    <Box
      justifyContent="center"
      height="100%"
      display="flex"
      flexDirection="row"
      justifyItems="left"
      alignItems="center"
    >
      {isMasterScenarioInMultiCompare && (
        <div
          style={{
            top: '-2px',
            width: '100%',
            position: 'absolute',
            borderTop: '1px solid black',
          }}
        />
      )}
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        px={3}
        borderLeftStyle="solid"
        borderLeftWidth="6px"
        borderLeftColor={borderColor}
        justifyContent="center"
        borderBottom={
          group.name != null && group.scenarioType !== 'scenario'
            ? '1px solid darkgray'
            : group.scenarioType === 'commitresult'
              ? '1px solid black'
              : undefined
        }
        flexGrow={1}
        data-testid={`group-title-${group.title}`}
      >
        <Text fontWeight="bold" color="neutral.black_700" isSingleLine>
          {group.title} {scenarioNameToLabel(group.scenarioType)}{' '}
          {group.name ?? ''}
        </Text>
        <Text fontSize="1" color="neutral.black_700" isSingleLine>
          {group.timeline?.subtitle ?? ''} {massCapacityLabel}
        </Text>
      </Box>
    </Box>
  );
};

const emptyAssignmentCleanupData = new Map();

const ScenarioMergeTimeline = forwardRef<
  ScenarioMergeTimelineRef,
  ScenarioMergeTimelineProps
>(
  (
    {
      scheduleContext,
      selectedScenarioData,
      masterData = selectedScenarioData,
      committedData,
      getScenarioData,
      onResetAssignmentsToMaster,
      filterTimelines,
      sharedOrderIds,
      onConfirmEventOverwrite,
      organizations,
      commitPolicy,
      mergeDirectives,
      moveShipmentAfterAnother,
    },
    ref,
  ): JSX.Element => {
    const currentUser = useCurrentUser();
    const euEtsCostEnabled = isEuEtsCostEnabledForUser(currentUser);
    const fuelEuCostEnabled =
      !!currentUser?.rootOrganization?.configIncludeFuelEuInCosts;
    const currentScenarioPnl = useCurrentScenarioPnl();

    const scenario: ScenarioSchedule | null = useMemo(
      () =>
        selectedScenarioData.scenario &&
        masterData.scenario &&
        (commitPolicy === ScenarioCommitPolicy.OVERRIDE
          ? selectedScenarioData.scenario
          : getMergedScenario(
              selectedScenarioData.scenario,
              masterData.scenario,
              {
                sourceIsCommit: true,
                directives: mergeDirectives ?? undefined,
              },
            )),
      [
        commitPolicy,
        selectedScenarioData.scenario,
        masterData.scenario,
        mergeDirectives,
      ],
    );

    const scenarioOrdersAndShipments = useMemo(() => {
      if (!scenario || !isUserScenario(scenario)) return null;

      const { committedOrderById, shipmentByClientId } = committedData;

      const { scenarioOrderById, scenarioShipmentByClientId } =
        getScenarioOrdersAndShipments(
          committedOrderById,
          shipmentByClientId,
          scenario,
          {
            ignoreIntegrationCargoMargins:
              !!currentUser?.organization?.configIgnoreIntegrationCargoMargins,
          },
        );

      return { scenarioOrderById, scenarioShipmentByClientId };
    }, [scenario, committedData, currentUser]);

    const tentativeCommitInput = useMemo(() => {
      if (
        !scenario ||
        !isUserScenario(scenario) ||
        !commitPolicy ||
        !currentUser ||
        !scenarioOrdersAndShipments
      ) {
        return null;
      }
      const { scenarioOrderById, scenarioShipmentByClientId } =
        scenarioOrdersAndShipments;

      return createCommitInput(
        committedData,
        scenario,
        null,
        scenarioShipmentByClientId,
        scenarioOrderById,
        emptyAssignmentCleanupData,
        {
          currentUser,
          policy: commitPolicy,
          suppressEmail: true,
        },
      );
    }, [
      scenario,
      commitPolicy,
      currentUser,
      scenarioOrdersAndShipments,
      committedData,
    ]);

    const mergedScenario = useMemo(
      () =>
        (masterData.scenario &&
          isMasterScenario(masterData.scenario) &&
          scenario &&
          isUserScenario(scenario) &&
          tentativeCommitInput &&
          previewMasterScenarioFromCommitInput(
            masterData.scenario,
            scenario,
            committedData,
            tentativeCommitInput.commit,
          )) ||
        null,
      [masterData.scenario, committedData, scenario, tentativeCommitInput],
    );

    const mergedScenarioData = useMemo(
      () => mergedScenario && getScenarioData(mergedScenario),
      [getScenarioData, mergedScenario],
    );

    const timelineDiff = useMemo(
      () =>
        selectedScenarioData.scenario &&
        getScenarioTimelinesDiff(
          masterData.timelines,
          selectedScenarioData.timelines,
          selectedScenarioData.scenario,
        ),
      [masterData, selectedScenarioData],
    );

    const changedTimelinesMaster = useMemo(
      () =>
        timelineDiff &&
        masterData?.timelines.filter((timeline) =>
          hasTimelineChanged(timeline, timelineDiff),
        ),
      [masterData, timelineDiff],
    );

    const changedTimelinesScenario = useMemo(
      () =>
        timelineDiff &&
        selectedScenarioData?.timelines.filter((timeline) =>
          hasTimelineChanged(timeline, timelineDiff),
        ),
      [selectedScenarioData, timelineDiff],
    );

    const allChangedTimelineIds = useMemo(
      () =>
        new Set([
          ...(changedTimelinesMaster?.map((t) => t.id) ?? []),
          ...(changedTimelinesScenario?.map((t) => t.id) ?? []),
        ]),
      [changedTimelinesMaster, changedTimelinesScenario],
    );

    const allMasterTimelinesById = useMemo(
      () => mapKeyBy(masterData.timelines, (t) => t.id),
      [masterData.timelines],
    );
    const allScenarioTimelinesById = useMemo(
      () => mapKeyBy(selectedScenarioData.timelines, (t) => t.id),
      [selectedScenarioData.timelines],
    );
    const allMergedScenarioTimelinesById = useMemo(
      () => mapKeyBy(mergedScenarioData?.timelines ?? [], (t) => t.id),
      [mergedScenarioData?.timelines],
    );

    const groupTriples = useMemo<
      [
        ScenarioMergeTimelineGroup,
        ScenarioMergeTimelineGroup,
        ScenarioMergeTimelineGroup,
      ][]
    >(
      () =>
        [...allChangedTimelineIds].map((timelineId) => {
          const masterTimeline = allMasterTimelinesById.get(timelineId);
          const scenarioTimeline = allScenarioTimelinesById.get(timelineId);
          const mergedScenarioTimeline =
            allMergedScenarioTimelinesById.get(timelineId);

          const result: [
            ScenarioMergeTimelineGroup,
            ScenarioMergeTimelineGroup,
            ScenarioMergeTimelineGroup,
          ] = [
            {
              id: generateGroupId(timelineId, 'master'),
              title: masterTimeline?.title ?? '',
              scenarioType: 'master',
              timeline: masterTimeline ?? null,
              stackItems: true,
            },
            {
              id: generateGroupId(timelineId, 'scenario'),
              title: scenarioTimeline?.title ?? '',
              scenarioType: 'scenario',
              timeline: scenarioTimeline ?? null,
              stackItems: true,
            },
            {
              id: generateGroupId(timelineId, 'commitresult'),
              title: mergedScenarioTimeline?.title ?? '',
              scenarioType: 'commitresult',
              timeline: mergedScenarioTimeline ?? null,
              stackItems: true,
            },
          ];

          return result;
        }),
      [
        allMasterTimelinesById,
        allScenarioTimelinesById,
        allMergedScenarioTimelinesById,
        allChangedTimelineIds,
      ],
    );

    const filteredGroupTriples = useMemo(
      () =>
        groupTriples.filter(
          ([masterGroup, scenarioGroup]) =>
            (!!masterGroup.timeline && filterTimelines(masterGroup.timeline)) ||
            (!!scenarioGroup.timeline &&
              filterTimelines(scenarioGroup.timeline)),
        ),
      [filterTimelines, groupTriples],
    );

    const filteredGroups = useMemo(
      () => filteredGroupTriples.flat(),
      [filteredGroupTriples],
    );

    const [expandedShipmentIds, setExpandedShipmentIds] = useState<
      Set<ClientId>
    >(new Set());

    const mergedOverlappingShipments = useMemo(() => {
      const result = new DefaultMap<ClientId, Set<ClientId>>(() => new Set());
      for (const timeline of mergedScenarioData?.timelines ?? []) {
        if (timeline.type !== 'tc') continue;
        const tds = timeline.voyageGroups
          .flatMap((vg) => vg.events)
          .sort((a, b) => a.start - b.start);
        const activeOrdersByShipment = new Map<ClientId, number>();
        for (const td of tds) {
          if (isTCOrderEventTimeData(td) && td.event.order.shipmentId) {
            const shipmentId = td.event.order.shipmentId;
            const activeOrders = activeOrdersByShipment.get(shipmentId) ?? 0;
            if (isLoadEventTimeData(td)) {
              for (const otherShipmentId of activeOrdersByShipment.keys()) {
                if (otherShipmentId === shipmentId) continue;
                result.get(otherShipmentId).add(shipmentId);
                result.get(shipmentId).add(otherShipmentId);
              }
              activeOrdersByShipment.set(shipmentId, activeOrders + 1);
            } else {
              if (activeOrders === 1) {
                activeOrdersByShipment.delete(shipmentId);
              } else {
                activeOrdersByShipment.set(shipmentId, activeOrders - 1);
              }
            }
          }
        }
      }
      return result;
    }, [mergedScenarioData]);

    const moveShipmentAfterOverlapping = useCallback(
      (shipmentToMove: ClientId, placeAfter: boolean) => {
        if (placeAfter) {
          const firstOverlappingShipment = [
            ...(mergedOverlappingShipments.get(shipmentToMove) ?? []),
          ].at(0);
          if (firstOverlappingShipment) {
            moveShipmentAfterAnother(shipmentToMove, firstOverlappingShipment);
          }
        } else {
          moveShipmentAfterAnother(shipmentToMove, null);
        }
      },
      [mergedOverlappingShipments, moveShipmentAfterAnother],
    );

    const items = useMemo<ScenarioMergeTimelineItem[]>(
      () =>
        (
          [
            [masterData, 'master'],
            [selectedScenarioData, 'scenario'],
            [mergedScenarioData, 'commitresult'],
          ] as const
        ).flatMap(([dataSet, scenarioName]) => {
          if (!dataSet?.scenario || !timelineDiff) {
            return [];
          }
          return buildAssignedItems({
            expandedShipmentIds,
            sharedOrderIds,
            timelines: dataSet.timelines
              .filter((t) => allChangedTimelineIds.has(t.id))
              .filter(filterTimelines),
            groups: filteredGroups
              .filter(
                (
                  g,
                ): g is ScenarioMergeTimelineGroup & {
                  timeline: NonNullable<EventTimeline>;
                } => !!g.timeline && g.scenarioType === scenarioName,
              )
              .map((g) => ({ ...g, suggestions: [] })),
            timelineDiff: scenarioName !== 'commitresult' ? timelineDiff : null,
            organizations,
            scenarioType: scenarioName,
            distances: scheduleContext.distances,
            orderById: scenarioOrdersAndShipments?.scenarioOrderById ?? null,
            euEtsCostEnabled,
            fuelEuCostEnabled,
            pnlByShipmentId: currentScenarioPnl?.byShipmentId,
          });
        }),
      [
        allChangedTimelineIds,
        currentScenarioPnl?.byShipmentId,
        euEtsCostEnabled,
        expandedShipmentIds,
        filterTimelines,
        filteredGroups,
        fuelEuCostEnabled,
        masterData,
        mergedScenarioData,
        organizations,
        scenarioOrdersAndShipments?.scenarioOrderById,
        scheduleContext.distances,
        selectedScenarioData,
        sharedOrderIds,
        timelineDiff,
      ],
    );

    return TimelineComparison(
      items,
      onResetAssignmentsToMaster,
      ref,
      scheduleContext.locations,
      filteredGroups,
      expandedShipmentIds,
      mergeDirectives,
      moveShipmentAfterOverlapping,
      setExpandedShipmentIds,
      onConfirmEventOverwrite,
    );
  },
);

function TimelineComparison(
  items: ScenarioMergeTimelineItem[],
  onResetAssignmentsToMaster: (items: ResetAssignmentsToMasterOpts) => void,
  ref:
    | ((instance: ScenarioMergeTimelineRef | null) => void)
    | React.MutableRefObject<ScenarioMergeTimelineRef | null>
    | null,
  locations: Map<number, ScenarioLocation>,
  filteredGroups: ScenarioMergeTimelineGroup[],
  expandedShipmentIds: Set<number | ClientId>,
  mergeDirectives: MergeDirectives | null,
  moveShipmentAfterOverlapping:
    | null
    | ((shipmentToMove: ClientId, placeAfter: boolean) => void),
  setExpandedShipmentIds: (
    value:
      | ((prevState: Set<number | ClientId>) => Set<number | ClientId>)
      | Set<number | ClientId>,
  ) => void,
  onConfirmEventOverwrite: (events: TimeData[]) => void,
) {
  // Sort groups by sortId and volumeCapacity
  const sortedGroups = useMemo(() => {
    const groups = filteredGroups.flat();
    return groups.sort((a, b) => {
      const sortIdA = Number(a.timeline?.vessel?.sortId ?? 0);
      const sortIdB = Number(b.timeline?.vessel?.sortId ?? 0);
      const volCapA = Number(a.timeline?.vessel?.volumeCapacity ?? 0);
      const volCapB = Number(b.timeline?.vessel?.volumeCapacity ?? 0);
      const sortIdCompare = sortIdB - sortIdA;
      const volCapCompare = volCapB - volCapA;
      return sortIdCompare || volCapCompare;
    });
  }, [filteredGroups]);

  const handleResetItemToMaster = (
    itemId: ScenarioTimelineItem['id'],
    itemType: 'order' | 'shipment',
  ) => {
    const item = items.find((item) => item.id === itemId);
    assertDefined(item, 'item');

    if ('orders' in item && item.type !== ItemType.COMPLETED) {
      if (itemType === 'shipment') {
        onResetAssignmentsToMaster({
          shipmentIds: compact(
            uniq(item.orders.map((order) => order.shipmentId)),
          ),
        });
      } else {
        onResetAssignmentsToMaster({
          orderIds: item.orders.map((order) => order.id),
        });
      }
    } else {
      throw new Error(
        `Resetting items of type ${item.type} (id ${item.id}) not supported`,
      );
    }
  };

  const timelineRef =
    useRef<
      ITimelineView<ScenarioMergeTimelineItem, ScenarioMergeTimelineGroup>
    >(null);

  useImperativeHandle(ref, () => ({
    scrollToToday: () => scrollToToday(timelineRef),
  }));

  const [observer, setObserver] = useState<CustomObserver | undefined>();

  const [timelineScroller, setTimelineScroller] = useState<Element>();

  const timelineScrollRef = (ref: HTMLDivElement) => {
    setTimelineScroller(ref);
    prepareIntersectionObserver(observer, ref, setObserver);
  };

  const { getTimelineOffsetTop, scrollToTimeline } = useTimelineHandlers(
    timelineRef,
    sortedGroups,
    timelineScroller,
  );

  const itemRenderer = useMemo(
    () =>
      makeTimelineItemRenderer({
        getTimelineOffsetTop,
        locations,
        scrollToTimeline,
        observer,
        showInquiries: false,
        shipmentIntegrationStatus: null,
      }),
    [getTimelineOffsetTop, locations, scrollToTimeline, observer],
  );

  const itemById = useMemo(() => keyBy(items, (i) => i.id), [items]);

  // Item that context menu was opened on
  const [contextItemId, setContextItemId] = useState<
    ScenarioTimelineItem['id'] | null
  >(null);

  const contextItem: ScenarioTimelineItem | null = useMemo(
    () => (contextItemId ? (itemById[contextItemId] ?? null) : null),
    [contextItemId, itemById],
  );

  const canResetContextItem = !!(
    contextItem &&
    'combinedDiff' in contextItem &&
    contextItem.combinedDiff?.hasChange
  );

  const {
    onContextMenu: onItemContextMenu,
    styles: itemMenuStyles,
    isVisible: isItemMenuVisible,
    hideMenu: hideItemContextMenu,
    setMenuRef: setItemMenuRef,
  } = useContextMenu();

  const handleItemContextMenu = (
    itemId: ScenarioTimelineItem['id'],
    event: React.MouseEvent,
  ) => {
    const item = itemById[itemId];
    if (item?.isShadow) return;

    if (!item) {
      throw new Error(`Timeline item not found by id`);
    }
    setContextItemId(itemId);
    if (item.type === ItemType.SCHEDULED_ORDER) {
      onItemContextMenu(event);
    } else if (item.type === ItemType.SPOT) {
      onItemContextMenu(event);
    } else if (item.type === ItemType.COA) {
      onItemContextMenu(event);
    } else {
      setContextItemId(null);
    }
  };

  return (
    <>
      <Box display="flex" flexDirection="column" flex={1} height="100%">
        <TimelineStyle />
        <VoyageMenuContext.Provider value={useContextStore({})}>
          <TimelineView<ScenarioMergeTimelineItem, ScenarioMergeTimelineGroup>
            timelineRef={timelineRef}
            scrollRef={timelineScrollRef}
            groups={sortedGroups}
            items={items}
            canMove={false}
            headerHeight={57}
            sidebarWidth={200}
            selected={[]}
            itemRenderer={itemRenderer}
            groupRenderer={groupRenderer}
            resizeDetector={TimelineResizeDetector}
            onItemContextMenu={handleItemContextMenu}
          ></TimelineView>
        </VoyageMenuContext.Provider>
      </Box>

      <div ref={setItemMenuRef} style={itemMenuStyles}>
        {isItemMenuVisible && contextItem !== null && (
          <Menu hasShadow>
            {'orders' in contextItem &&
              contextItem.orders[0]?.shipmentId != null && (
                <>
                  {expandedShipmentIds.has(contextItem.orders[0].shipmentId) ? (
                    <MenuItem
                      label="Collapse shipment"
                      onPress={() => {
                        setExpandedShipmentIds((curCollapsedShipmentIds) => {
                          if (contextItem.orders[0]?.shipmentId == null) {
                            console.error(
                              'Tried to collapse shipment, but no shipmentId found',
                              contextItem,
                            );
                            return curCollapsedShipmentIds;
                          }

                          const s = new Set(curCollapsedShipmentIds);
                          s.delete(contextItem.orders[0].shipmentId);
                          return s;
                        });
                        hideItemContextMenu();
                      }}
                    />
                  ) : (
                    <MenuItem
                      label="Expand shipment"
                      onPress={() => {
                        setExpandedShipmentIds((curCollapsedShipmentIds) => {
                          if (contextItem.orders[0]?.shipmentId == null) {
                            console.error(
                              'Tried to expand shipment, but no shipmentId found',
                              contextItem,
                            );
                            return curCollapsedShipmentIds;
                          }

                          const s = new Set(curCollapsedShipmentIds);
                          s.add(contextItem.orders[0].shipmentId);
                          return s;
                        });
                        hideItemContextMenu();
                      }}
                    />
                  )}
                  {mergeDirectives?.placeShipmentAfter?.has(
                    contextItem.orders[0].shipmentId,
                  ) ? (
                    <MenuItem
                      label="Undo resolve overlap"
                      onPress={() => {
                        if (contextItem.orders[0]?.shipmentId == null) {
                          console.error(
                            'Tried to alter shipment position, but no shipmentId found',
                            contextItem,
                          );
                          return;
                        }
                        moveShipmentAfterOverlapping?.(
                          contextItem.orders[0].shipmentId,
                          false,
                        );
                        hideItemContextMenu();
                      }}
                    />
                  ) : (
                    <MenuItem
                      label="Move after overlapping voyage"
                      onPress={() => {
                        if (contextItem.orders[0]?.shipmentId == null) {
                          console.error(
                            'Tried to alter shipment position, but no shipmentId found',
                            contextItem,
                          );
                          return;
                        }
                        moveShipmentAfterOverlapping?.(
                          contextItem.orders[0].shipmentId,
                          true,
                        );
                        hideItemContextMenu();
                      }}
                    />
                  )}
                </>
              )}
            {!('isCollapsed' in contextItem && contextItem.isCollapsed) && (
              <MenuItem
                label="Use committed version (order)"
                onPress={() => {
                  if (contextItemId !== null) {
                    handleResetItemToMaster(contextItemId, 'order');
                  }
                  hideItemContextMenu();
                }}
              />
            )}
            {canResetContextItem && (
              <MenuItem
                label="Use committed version (Whole shipment)"
                onPress={() => {
                  if (contextItemId !== null) {
                    handleResetItemToMaster(contextItemId, 'shipment');
                  }
                  hideItemContextMenu();
                }}
              />
            )}
            {'combinedDiff' in contextItem &&
              filteredGroups.find((g) => g.id === contextItem.group)
                ?.scenarioType === 'scenario' &&
              'events' in contextItem &&
              contextItem.combinedDiff?.hasConflict && (
                <MenuItem
                  label="Confirm overwrite"
                  onPress={() => {
                    if (contextItemId !== null) {
                      onConfirmEventOverwrite(contextItem.events);
                    }
                    hideItemContextMenu();
                  }}
                />
              )}
          </Menu>
        )}
      </div>
    </>
  );
}

export default ScenarioMergeTimeline;

type ItemData = [
  ScenarioSchedule | undefined,
  EventTimeline[] | undefined,
  ScenarioType,
  ScenarioTimelineDiff | undefined,
];
export const CompareScenarioTimelines = forwardRef<
  ScenarioMergeTimelineRef,
  Omit<
    ScenarioMergeTimelineProps,
    'filterTimelines' | 'moveShipmentAfterAnother' | 'mergeDirectives'
  > & {
    selectedScenarios: ClientId[];
    baseScenarioId?: ClientId;
    scenarios: readonly ScenarioSchedule[];
    timelinesByScenario: Map<ClientId, EventTimeline[]>;
    calculateSubTimeline: (timeline: EventTimeline) => EventTimeline;
    includeResult?: boolean;
    filterTimelines?: ScenarioMergeTimelineProps['filterTimelines'];
  }
>(
  (
    {
      scheduleContext,
      selectedScenarios,
      baseScenarioId,
      scenarios,
      selectedScenarioData,
      masterData = selectedScenarioData,
      committedData,
      getScenarioData,
      onResetAssignmentsToMaster,
      filterTimelines,
      sharedOrderIds,
      onConfirmEventOverwrite,
      organizations,
      commitPolicy,
      timelinesByScenario,
      calculateSubTimeline,
      includeResult,
    },
    ref,
  ): JSX.Element => {
    commitPolicy ??= ScenarioCommitPolicy.OVERRIDE;
    const masterTimeline =
      timelinesByScenario.get(MASTER_SCENARIO_CLIENTID) ?? masterData.timelines;
    const masterScenario =
      scenarios.find(isMasterScenario) ?? masterData.scenario;

    const currentUser = useCurrentUser();
    const euEtsCostEnabled = isEuEtsCostEnabledForUser(currentUser);
    const fuelEuCostEnabled =
      !!currentUser?.rootOrganization?.configIncludeFuelEuInCosts;
    const currentScenarioPnl = useCurrentScenarioPnl();

    const compareScenarios: (UserSchedule | SuggestionSchedule)[] =
      useMemo(() => {
        return scenarios
          .filter(composeTypeGuardOr(isUserScenario, isSuggestionScenario))
          .filter((scenario) => selectedScenarios.includes(scenario.clientId));
      }, [scenarios, selectedScenarios]);

    const syncedUserScenarios = useMemo(
      () =>
        masterScenario &&
        (commitPolicy === ScenarioCommitPolicy.OVERRIDE
          ? compareScenarios
          : compareScenarios.map(
              (scenario) =>
                getMergedScenario(scenario, masterScenario, {
                  sourceIsCommit: true,
                }) as UserSchedule | SuggestionSchedule,
            )),
      [commitPolicy, compareScenarios, masterScenario],
    );

    const tentativeCommitInputs = useMemo(() => {
      if (!syncedUserScenarios || !commitPolicy || !currentUser) return null;
      const { committedOrderById, shipmentByClientId } = committedData;
      return syncedUserScenarios.map((scenario) => {
        const { scenarioOrderById, scenarioShipmentByClientId } =
          getScenarioOrdersAndShipments(
            committedOrderById,
            shipmentByClientId,
            scenario,
            {
              ignoreIntegrationCargoMargins:
                !!currentUser.organization?.configIgnoreIntegrationCargoMargins,
            },
          );

        return {
          scenarioOrderById,
          commitInput: createCommitInput(
            committedData,
            scenario,
            null,
            scenarioShipmentByClientId,
            scenarioOrderById,
            emptyAssignmentCleanupData,
            {
              currentUser,
              policy: commitPolicy,
              suppressEmail: true,
            },
          ),
        };
      });
    }, [syncedUserScenarios, committedData, currentUser, commitPolicy]);

    const mergedScenarios = useMemo(() => {
      if (!isMasterScenario(masterScenario)) return null;
      return (
        (tentativeCommitInputs &&
          tentativeCommitInputs.map((tentativeCommitInput, index) =>
            previewMasterScenarioFromCommitInput(
              masterScenario,
              syncedUserScenarios[index],
              committedData,
              tentativeCommitInput.commitInput.commit,
            ),
          )) ||
        null
      );
    }, [
      masterScenario,
      committedData,
      syncedUserScenarios,
      tentativeCommitInputs,
    ]);

    const mergedScenarioData = useMemo(
      () =>
        mergedScenarios &&
        mergedScenarios.map((mergedScenario) => {
          const scenarioData = getScenarioData(mergedScenario);
          return {
            ...scenarioData,
            timelines: scenarioData.timelines.map(calculateSubTimeline),
          };
        }),
      [calculateSubTimeline, getScenarioData, mergedScenarios],
    );

    const scenarioTimelines = useMemo(
      () =>
        compareScenarios.map((scenario) =>
          timelinesByScenario.get(scenario.clientId),
        ),
      [compareScenarios, timelinesByScenario],
    );

    const baseTimelines = useMemo(
      () =>
        timelinesByScenario.get(baseScenarioId ?? MASTER_SCENARIO_CLIENTID) ??
        masterTimeline,
      [baseScenarioId, masterTimeline, timelinesByScenario],
    );

    const timelineDiffs = useMemo(
      () =>
        compareScenarios.map((scenario, index) => {
          const timelines = scenarioTimelines[index];
          return (
            timelines &&
            getScenarioTimelinesDiff(baseTimelines, timelines, scenario)
          );
        }),
      [compareScenarios, scenarioTimelines, baseTimelines],
    );

    const changedTimelinesBase = useMemo(
      () =>
        timelineDiffs &&
        baseTimelines.filter((timeline) =>
          timelineDiffs.some(
            (timelineDiff) =>
              timelineDiff && hasTimelineChanged(timeline, timelineDiff),
          ),
        ),
      [baseTimelines, timelineDiffs],
    );

    const changedTimelinesScenarios = useMemo(
      () =>
        timelineDiffs &&
        scenarioTimelines.map((timelines, index) =>
          timelines?.filter((timeline) => {
            const timelineDiff = timelineDiffs[index];
            return (
              timeline &&
              timelineDiff &&
              hasTimelineChanged(timeline, timelineDiff)
            );
          }),
        ),
      [scenarioTimelines, timelineDiffs],
    );

    const allChangedTimelineIds = useMemo(
      () =>
        new Set([
          ...(changedTimelinesBase?.map((t) => t.id) ?? []),
          ...changedTimelinesScenarios?.flatMap(
            (changedTimelinesScenario) =>
              changedTimelinesScenario?.map((t) => t.id) ?? [],
          ),
        ]),
      [changedTimelinesBase, changedTimelinesScenarios],
    );

    const allBaseTimelinesById = useMemo(
      () => mapKeyBy(baseTimelines, (t) => t.id),
      [baseTimelines],
    );
    const allScenarioTimelinesById = useMemo(
      () =>
        scenarioTimelines.map(
          (timelines) => timelines && mapKeyBy(timelines, (t) => t.id),
        ),
      [scenarioTimelines],
    );
    const allMergedScenarioTimelinesById = useMemo(
      () =>
        mergedScenarioData?.map((mergedScenario) =>
          mapKeyBy(mergedScenario.timelines ?? [], (t) => t.id),
        ),
      [mergedScenarioData],
    );

    const mergeGroups = useMemo<ScenarioMergeTimelineGroup[][]>(() => {
      const includeMaster = selectedScenarios.includes(
        MASTER_SCENARIO_CLIENTID,
      );
      return [...allChangedTimelineIds].map((timelineId) => {
        const baseTimeline = allBaseTimelinesById.get(timelineId);

        const result: ScenarioMergeTimelineGroup[] = allScenarioTimelinesById
          .map((scenarioTimelinesById, index): ScenarioMergeTimelineGroup[] => {
            const scenario = compareScenarios[index];
            const scenarioTimeline = scenarioTimelinesById?.get(timelineId);
            const mergedScenarioTimeline =
              allMergedScenarioTimelinesById?.[index]?.get(timelineId);
            const scenarioGroup: ScenarioMergeTimelineGroup = {
              id: generateGroupId(timelineId + scenario.name, 'scenario'),
              title: scenarioTimeline?.title ?? '',
              scenarioType: 'scenario',
              timeline: scenarioTimeline ?? null,
              stackItems: true,
              name: scenario.name,
            };
            return !includeResult
              ? [scenarioGroup]
              : [
                  scenarioGroup,
                  {
                    id: generateGroupId(
                      timelineId + scenario.name,
                      'commitresult',
                    ),
                    title: mergedScenarioTimeline?.title ?? '',
                    scenarioType: 'commitresult',
                    timeline: mergedScenarioTimeline ?? null,
                    stackItems: true,
                    name: scenario.name,
                  },
                ];
          })
          .flat();

        if (includeMaster) {
          result.unshift({
            id: generateGroupId(timelineId, 'master'),
            title: baseTimeline?.title ?? '',
            scenarioType: 'master',
            timeline: baseTimeline ?? null,
            stackItems: true,
            name: '',
          });
        }

        return result;
      });
    }, [
      selectedScenarios,
      allChangedTimelineIds,
      allBaseTimelinesById,
      allScenarioTimelinesById,
      compareScenarios,
      allMergedScenarioTimelinesById,
      includeResult,
    ]);

    const filteredMergeGroups = useMemo(
      () =>
        mergeGroups.map((groups) =>
          groups.filter(
            (group) =>
              !!group.timeline &&
              (!filterTimelines || filterTimelines(group.timeline)),
          ),
        ),
      [filterTimelines, mergeGroups],
    );

    const filteredGroups = useMemo(
      () => filteredMergeGroups.flat(),
      [filteredMergeGroups],
    );

    const [expandedShipmentIds, setExpandedShipmentIds] = useState<
      Set<number | ClientId>
    >(new Set());

    const items = useMemo<ScenarioMergeTimelineItem[]>(() => {
      const includeMaster = selectedScenarios.includes(
        MASTER_SCENARIO_CLIENTID,
      );
      const master: ItemData[] = includeMaster
        ? [[masterScenario, masterTimeline, 'master', timelineDiffs[0]]]
        : [];
      const itemData: ItemData[] = !timelineDiffs
        ? []
        : [
            ...master,
            ...(!mergedScenarioData
              ? []
              : compareScenarios
                  .map((scenario, index): ItemData[] => {
                    return !includeResult
                      ? [
                          [
                            scenario,
                            scenarioTimelines[index],
                            'scenario',
                            timelineDiffs[index],
                          ],
                        ]
                      : [
                          [
                            scenario,
                            scenarioTimelines[index],
                            'scenario',
                            timelineDiffs[index],
                          ],
                          [
                            mergedScenarioData[index].scenario,
                            mergedScenarioData[index].timelines,
                            'commitresult',
                            undefined,
                          ],
                        ];
                  })
                  .flat()),
          ];
      return itemData.flatMap(
        ([scenario, timelines, scenarioName, timelineDiff], index) => {
          if (!scenario || !timelines) {
            return [];
          }
          return buildAssignedItems({
            expandedShipmentIds,
            sharedOrderIds,
            timelines: timelines
              .filter((t) => allChangedTimelineIds.has(t.id))
              .filter((t) => !filterTimelines || filterTimelines(t)),
            groups: filteredGroups
              .filter(
                (
                  g,
                ): g is ScenarioMergeTimelineGroup & {
                  timeline: NonNullable<EventTimeline>;
                } => !!g.timeline && g.scenarioType === scenarioName,
              )
              .map((g) => ({ ...g, suggestions: [] })),
            timelineDiff: timelineDiff ?? null,
            organizations,
            scenarioType: scenarioName,
            scenarioName: scenario.name,
            distances: scheduleContext.distances,
            orderById:
              tentativeCommitInputs?.[index]?.scenarioOrderById ?? null,
            euEtsCostEnabled,
            fuelEuCostEnabled,
            pnlByShipmentId: currentScenarioPnl?.byShipmentId,
          });
        },
      );
    }, [
      selectedScenarios,
      masterScenario,
      masterTimeline,
      timelineDiffs,
      mergedScenarioData,
      compareScenarios,
      includeResult,
      scenarioTimelines,
      expandedShipmentIds,
      sharedOrderIds,
      filteredGroups,
      organizations,
      scheduleContext.distances,
      tentativeCommitInputs,
      euEtsCostEnabled,
      fuelEuCostEnabled,
      currentScenarioPnl?.byShipmentId,
      allChangedTimelineIds,
      filterTimelines,
    ]);

    return TimelineComparison(
      items,
      onResetAssignmentsToMaster,
      ref,
      scheduleContext.locations,
      filteredGroups,
      expandedShipmentIds,
      null,
      null,
      setExpandedShipmentIds,
      onConfirmEventOverwrite,
    );
  },
);
